// Utilitaire de génération d'URLs SEO pour les annonces d'emploi
// Format cible :
//   /bnssa/offres-emploi/<title>-<ville>-<departement>-<id>
//   /maitre-nageur-sauveteur/offres-emploi/<title>-<ville>-<departement>-<id>
//
// Exemple :
//   /bnssa/offres-emploi/surveillant-baignade-niort-79-5
//   /maitre-nageur-sauveteur/offres-emploi/bpjeps-aan-aquafitness-lyon-69-6

import type { Job, JobCategory } from "@/data/jobs";

/**
 * Slugifie une chaîne pour usage en URL :
 *  - normalisation Unicode (suppression accents)
 *  - minuscules
 *  - alphanum + tirets uniquement
 *  - tirets multiples consolidés
 *  - tirets de bord supprimés
 */
export function slugifyText(input: string): string {
  if (!input) return "";
  return input
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // accents
    .replace(/['’`"]/g, " ") // apostrophes/quotes -> espace
    .replace(/&/g, " et ")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Extrait le numéro de département depuis une chaîne du type
 * "Rhône (69)" ou "Pyrénées-Atlantiques (64)". Renvoie "" si absent.
 */
export function extractDepartmentCode(department: string): string {
  if (!department) return "";
  const m = department.match(/\((\d{2,3}[AB]?)\)/i);
  return m ? m[1].toLowerCase() : "";
}

/**
 * Préfixe de catégorie pour l'URL SEO.
 */
export function categoryPathPrefix(category: JobCategory): string {
  return category === "BNSSA" ? "/bnssa/offres-emploi" : "/maitre-nageur-sauveteur/offres-emploi";
}

/**
 * Construit le segment de département pour l'URL : nom EN LETTRES + code EN CHIFFRES.
 * Ex: "Rhône (69)" -> "rhone-69" ; "Pyrénées-Atlantiques (64)" -> "pyrenees-atlantiques-64".
 * Si aucun code n'est présent, on retombe sur le nom seul slugifié ; si seul le code
 * est disponible, on renvoie le code seul.
 */
export function buildDepartmentSlug(department: string): string {
  if (!department) return "";
  const code = extractDepartmentCode(department);
  // Nom du département sans la partie "(code)" finale, puis slugifié.
  const namePart = slugifyText(department.replace(/\([^)]*\)\s*$/, "").trim());
  return [namePart, code].filter(Boolean).join("-").replace(/-+/g, "-");
}

/**
 * Construit le slug complet d'une annonce (sans le préfixe de route).
 * Ex: "surveillant-baignade-niort-rhone-69-5"
 */
export function buildJobSlug(opts: {
  id: string;
  title: string;
  location: string;
  department: string;
}): string {
  const titleSlug = slugifyText(opts.title);
  const citySlug = slugifyText(opts.location);
  // Département en lettres ET en chiffres (ex. "rhone-69").
  const depSlug = buildDepartmentSlug(opts.department);
  const idPart = String(opts.id);
  // Compose et nettoie une dernière fois les tirets multiples éventuels
  return [titleSlug, citySlug, depSlug, idPart]
    .filter(Boolean)
    .join("-")
    .replace(/-+/g, "-");
}

/**
 * Construit le path complet d'une annonce.
 * Ex: "/bnssa/offres-emploi/surveillant-baignade-niort-79-5"
 */
export function buildJobPath(job: Pick<Job, "id" | "title" | "location" | "department" | "category">): string {
  const slug = buildJobSlug({
    id: job.id,
    title: job.title,
    location: job.location,
    department: job.department,
  });
  return `${categoryPathPrefix(job.category)}/${slug}`;
}

/**
 * Variante prenant des champs partiels (utile depuis recruiter_jobs ou applications snapshots).
 * Retourne null si l'id est absent.
 */
export function buildJobPathFromParts(opts: {
  id?: string;
  title?: string;
  location?: string;
  department?: string;
  category?: JobCategory;
}): string | null {
  if (!opts.id) return null;
  const category = opts.category || "BNSSA";
  const slug = buildJobSlug({
    id: opts.id,
    title: opts.title || "offre",
    location: opts.location || "",
    department: opts.department || "",
  });
  const prefix = categoryPathPrefix(category);
  return `${prefix}/${slug}`;
}

/**
 * Extrait l'ID de l'annonce depuis la dernière partie du slug.
 * Le format est `...-<id>` où `<id>` est soit numérique court, soit
 * un ObjectId Mongo (24 hex). On prend donc le dernier segment après
 * le dernier tiret.
 */
export function extractIdFromSlug(slug: string | undefined): string | null {
  if (!slug) return null;
  const parts = slug.split("-").filter(Boolean);
  if (parts.length === 0) return null;
  return parts[parts.length - 1];
}
